"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveEngineAssetUrl } from "@/config";
import { cn } from "@/lib/utils";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";

interface NoteGalleryPanelProps {
  images: string[];
  className?: string;
  /** Minimum height so a single image can fill the column. */
  minHeightClassName?: string;
}

/**
 * Column-filling gallery for Note with Gallery:
 * - 1 image → cover the whole panel
 * - 2+ images → full-bleed carousel
 */
export function NoteGalleryPanel({
  images,
  className,
  minHeightClassName = "min-h-[280px] sm:min-h-[360px]",
}: NoteGalleryPanelProps) {
  const resolved = useMemo(
    () => (images || []).map(resolveEngineAssetUrl).filter(Boolean),
    [images]
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index > resolved.length - 1) {
      setIndex(Math.max(0, resolved.length - 1));
    }
  }, [resolved.length, index]);

  if (resolved.length === 0) return null;

  const safeIndex = Math.min(index, resolved.length - 1);
  const showControls = resolved.length > 1;

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-xl bg-gray-100",
        minHeightClassName,
        "h-full",
        className
      )}
    >
      {resolved.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt=""
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            i === safeIndex ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        />
      ))}

      {showControls ? (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={() =>
              setIndex((prev) =>
                prev === 0 ? resolved.length - 1 : prev - 1
              )
            }
            className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-white shadow"
            style={quoteThemeColorStyles.primaryBg}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={() =>
              setIndex((prev) =>
                prev === resolved.length - 1 ? 0 : prev + 1
              )
            }
            className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-white shadow"
            style={quoteThemeColorStyles.primaryBg}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5">
            {resolved.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                onClick={() => setIndex(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === safeIndex ? "w-5" : "w-1.5 bg-white/70"
                )}
                style={
                  i === safeIndex
                    ? { backgroundColor: quoteThemeCssVars.primary }
                    : undefined
                }
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
